import {
  createClient,
  type AuthChangeEvent,
  type Session,
} from "@supabase/supabase-js";

const url = import.meta.env.VITE_SUPABASE_URL as string;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

if (!url || !anonKey) {
  console.warn(
    "[relais] VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY missing — auth will fail",
  );
}

export const supabase = createClient(url ?? "", anonKey ?? "", {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    storageKey: "relais-front-auth",
    // Épinglé : le flow recovery (lien mail → #access_token) dépend de
    // `implicit`. Passer à `pkce` casserait le reset cross-device (code-verifier
    // absent sur le device qui clique). Ne pas changer sans revoir /reset.
    flowType: "implicit",
  },
});

// --- Garde de la session recovery ---
//
// Le form /reset appelle `updateUser` SANS l'ancien mot de passe. Il ne doit
// donc s'ouvrir QUE pour LA session issue du lien recovery — pas :
//   - pour une session normale qui aurait vu passer un PASSWORD_RECOVERY plus
//     tôt dans l'historique de l'onglet (un simple booléen global ne distingue
//     pas « la session courante EST recovery » de « un recovery a eu lieu ») ;
//   - pour un hash `#type=recovery` fabriqué à la main (l'URL est spoofable et
//     ne prouve rien).
//
// On mémorise donc l'access_token de la session recovery — émis par Supabase
// APRÈS vérification serveur du token du lien — et /reset n'autorise le form que
// si la session COURANTE porte ce même token. Autorisation = match de token,
// jamais l'URL.
let recoveryToken: string | null = null;
const recoveryListeners = new Set<() => void>();

function setRecoveryToken(token: string | null) {
  if (recoveryToken === token) return;
  recoveryToken = token;
  recoveryListeners.forEach((l) => l());
}

supabase.auth.onAuthStateChange(
  (event: AuthChangeEvent, session: Session | null) => {
    if (event === "PASSWORD_RECOVERY") {
      setRecoveryToken(session?.access_token ?? null);
    } else if (event === "USER_UPDATED" || event === "SIGNED_OUT") {
      // USER_UPDATED = émis après un updateUser réussi → on consomme le jeton
      // pour qu'un retour sur /reset ne rouvre pas le form. SIGNED_OUT idem.
      setRecoveryToken(null);
    }
    // ponytail: TOKEN_REFRESHED non géré. Si le jeton tourne pendant que l'user
    // tape (rare : access_token ~1 h vs saisie de quelques secondes), le match
    // échoue et /reset retombe sur le loader (fail-safe, saisie perdue). On NE
    // re-stampe PAS sur refresh : sans le jeton pré-refresh on ne peut pas
    // prouver que la session rafraîchie ÉTAIT la session recovery, et re-stamper
    // à l'aveugle rouvrirait le form pour une session normale. À traiter (garder
    // le session_id recovery) seulement si le refresh-mid-saisie devient réel.
  },
);

// Réactif : /reset s'abonne via useSyncExternalStore et re-rend quand le jeton
// change — sinon un PASSWORD_RECOVERY arrivant APRÈS le premier rendu `authed`
// (getSession résout avant le setTimeout(0) qui émet l'event) ne corrigerait
// jamais la décision de redirect.
export function subscribeRecovery(onChange: () => void): () => void {
  recoveryListeners.add(onChange);
  return () => recoveryListeners.delete(onChange);
}

export function getRecoveryToken(): string | null {
  return recoveryToken;
}

// Indice SYNCHRONE capté au chargement du module (avant que detectSessionInUrl
// ne nettoie le hash) : un redirect recovery est en cours. Utilisé UNIQUEMENT
// pour retarder le redirect /profil le temps que PASSWORD_RECOVERY pose le jeton
// — JAMAIS pour autoriser le form. Un hash spoofé n'ouvre donc rien : au pire il
// affiche un loader qui ne se résout pas.
export const recoveryRedirectPending: boolean =
  typeof window !== "undefined" &&
  /(?:^|[#&])type=recovery(?:&|$)/.test(window.location.hash);
